/**
 * Data Quality Widget (issue #1655, part of epic #1650)
 *
 * Shows the average of the real `qualityScore` field already stored on
 * `DataDomain`/`DataObject`, matching `homepage-dashboard-spec.md`'s "Data
 * Quality Score" widget — no fabricated completeness/consistency/
 * timeliness/accuracy breakdown (see `backend/src/graphql/resolvers/
 * dataQuality.ts`'s module doc). Scoped to the current user's own teams by
 * default. Follows the single-query + `useStatisticsWidget` pattern shared
 * by `DependencyHealthWidget`.
 */

import { useDataQuality, type DataQualityIssue } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './DataQualityWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'data-quality',
  title: 'Data Quality',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface DataQualityStats {
  overallScore: number | null;
  scoredCount: number;
  totalCount: number;
  issues: DataQualityIssue[];
  scope: string;
}

type DataQualityQueryData = ReturnType<typeof useDataQuality>['data'];

function extractDataQualityStats(data: DataQualityQueryData): DataQualityStats | null {
  if (!data?.dataQuality) {
    return null;
  }
  return data.dataQuality;
}

export function DataQualityWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useDataQuality(),
    extractDataQualityStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.totalCount === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="data-quality-widget__empty">
          <IconElement name="gauge" className="data-quality-widget__empty-icon" />
          <p className="forge-typography--body2">No data quality scores available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="data-quality-widget__content">
        <p
          className="data-quality-widget__scope-label forge-typography--caption"
          data-testid="data-quality-scope"
        >
          Scope: {stats.scope}
        </p>

        <div className="data-quality-widget__score">
          <div className="data-quality-widget__score-number" data-testid="data-quality-score">
            {stats.overallScore ?? '—'}
          </div>
          <div className="data-quality-widget__score-label forge-typography--label2">
            Overall Score ({stats.scoredCount}/{stats.totalCount} scored)
          </div>
        </div>

        {stats.issues.length > 0 && (
          <ul className="data-quality-widget__issues" data-testid="data-quality-issues">
            {stats.issues.map((issue) => (
              <li key={issue.type + issue.example} className="data-quality-widget__issue-row">
                <span className="data-quality-widget__issue-count">{issue.count}</span>
                <span className="data-quality-widget__issue-example forge-typography--caption">
                  {issue.example}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardWidget>
  );
}
